import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Calendar, Users, Target, ExternalLink, Quote,
  CheckCircle, Sparkles, ChevronRight, BookOpen, Mic, Building2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { companyInfo } from '../data/mock';
import BrandLogo from '../components/BrandLogo';
import { CaseStudyCard, normalizeCaseStudy } from '../components/case-studies/CaseStudyCard';
import { CALENDLY_URL } from '../data/workshops';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [cs, setCs] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setNotFound(false);
    axios.get(`${API}/case-studies/by-slug/${slug}`)
      .then(({ data }) => {
        if (!mounted) return;
        setCs(data);
        // Apply SEO meta
        document.title = `${data.org_name} — Case Study | The WE Mentality`;
        const descContent = `${data.engagement_title || data.org_name}. ${data.outcome || data.description || ''}`.slice(0, 160);
        let meta = document.querySelector('meta[name="description"]');
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute('name', 'description');
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', descContent);

        // OG tags
        const setOg = (prop, content) => {
          let tag = document.querySelector(`meta[property="${prop}"]`);
          if (!tag) {
            tag = document.createElement('meta');
            tag.setAttribute('property', prop);
            document.head.appendChild(tag);
          }
          tag.setAttribute('content', content);
        };
        setOg('og:title', `${data.org_name} — Case Study`);
        setOg('og:description', descContent);
        setOg('og:type', 'article');
        if (data.hero_image) setOg('og:image', data.hero_image.startsWith('http') ? data.hero_image : `${window.location.origin}${data.hero_image}`);
        setOg('og:url', window.location.href);

        // JSON-LD structured data
        let ld = document.getElementById('case-study-ld');
        if (!ld) {
          ld = document.createElement('script');
          ld.id = 'case-study-ld';
          ld.type = 'application/ld+json';
          document.head.appendChild(ld);
        }
        ld.textContent = JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: `${data.org_name} — ${data.engagement_title || 'Case Study'}`,
          description: descContent,
          image: data.hero_image,
          author: { '@type': 'Organization', name: 'The WE Mentality' },
          publisher: { '@type': 'Organization', name: 'The WE Mentality' },
          about: { '@type': 'Organization', name: data.org_name, url: data.website_url || undefined },
          datePublished: data.created_at,
          dateModified: data.updated_at,
        });

        // Fetch related case studies (same category or industry)
        const cat = data.workshop_category;
        const ind = data.industry;
        axios.get(`${API}/case-studies`).then(({ data: listData }) => {
          if (!mounted) return;
          const others = (listData?.items || []).filter((it) => it.slug !== slug);
          const sameCategory = others.filter((it) => cat && it.workshop_category === cat);
          const sameIndustry = others.filter((it) => ind && it.industry === ind && (!cat || it.workshop_category !== cat));
          const fallback = others.filter((it) => !sameCategory.includes(it) && !sameIndustry.includes(it));
          setRelated([...sameCategory, ...sameIndustry, ...fallback].slice(0, 3));
        }).catch(() => {});
      })
      .catch((err) => {
        if (!mounted) return;
        console.error('[CaseStudyDetail] Fetch failed:', err?.message);
        setNotFound(true);
      })
      .finally(() => { if (mounted) setLoading(false); });

    return () => {
      mounted = false;
      // SEO cleanup: remove injected JSON-LD on unmount so it doesn't bleed into other routes
      const ld = document.getElementById('case-study-ld');
      if (ld) ld.remove();
      // Reset OG tags to a neutral state
      ['og:title', 'og:description', 'og:type', 'og:image', 'og:url'].forEach((p) => {
        const tag = document.querySelector(`meta[property="${p}"]`);
        if (tag) tag.remove();
      });
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !cs) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex flex-col items-center justify-center px-4">
        <h1 className="text-4xl font-bold mb-3">Case study not found</h1>
        <p className="text-slate-400 mb-6">The case study you're looking for doesn't exist or has been removed.</p>
        <Button onClick={() => navigate('/case-studies')} className="bg-gradient-to-r from-violet-600 to-cyan-500 text-white">
          ← Back to Case Studies
        </Button>
      </div>
    );
  }

  const hero = cs.hero_image || '/images/workshop-training.jpg';

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-30 py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <BrandLogo className="h-10 sm:h-12 w-auto" fallbackClassName="text-base sm:text-lg font-bold tracking-wide text-white whitespace-nowrap" testId="csd-header-logo-img" />
          </a>
          <div className="flex items-center gap-3">
            <Button onClick={() => navigate('/case-studies')} variant="ghost" className="text-slate-300 hover:text-white hover:bg-white/5" data-testid="csd-back-btn">
              <ArrowLeft className="h-4 w-4 mr-2" /> All Case Studies
            </Button>
            <Button onClick={() => window.open(CALENDLY_URL, '_blank')} className="hidden sm:inline-flex bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-500 hover:to-cyan-400 text-white font-semibold" data-testid="csd-nav-book">
              Book a Strategy Session
            </Button>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="pt-28 sm:pt-32 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        <nav className="flex items-center gap-1.5 text-sm text-slate-500" data-testid="csd-breadcrumb">
          <Link to="/" className="hover:text-cyan-400">Home</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <Link to="/case-studies" className="hover:text-cyan-400">Case Studies</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-slate-300">{cs.org_name}</span>
        </nav>
      </div>

      {/* ─────────────────────────── HERO ─────────────────────────── */}
      <section className="relative pt-10 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 right-1/4 w-[600px] h-[600px] bg-violet-500/10 rounded-full blur-[150px]" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto grid lg:grid-cols-12 gap-10">
          {/* Left: meta + title */}
          <div className="lg:col-span-7">
            {/* Logo + tags */}
            <div className="flex items-center gap-3 mb-6 flex-wrap">
              {cs.logo_url ? (
                <div className="h-14 px-4 rounded-xl bg-white flex items-center justify-center">
                  <img src={cs.logo_url} alt={`${cs.org_name} logo`} className="max-h-10 max-w-[160px] object-contain" />
                </div>
              ) : (
                <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-white" />
                </div>
              )}
              {cs.industry && (
                <Link
                  to={`/case-studies?industry=${encodeURIComponent(cs.industry)}`}
                  className="px-3 py-1.5 rounded-full bg-violet-500/15 border border-violet-500/30 text-violet-200 text-xs font-semibold uppercase tracking-wider hover:bg-violet-500/25 hover:border-violet-400 transition-all"
                  data-testid="csd-industry-tag"
                  title={`See all ${cs.industry} case studies`}
                >
                  {cs.industry}
                </Link>
              )}
              {cs.workshop_category && (
                <Link
                  to={`/case-studies?category=${encodeURIComponent(cs.workshop_category)}`}
                  className="px-3 py-1.5 rounded-full bg-cyan-500/15 border border-cyan-500/30 text-cyan-200 text-xs font-semibold uppercase tracking-wider hover:bg-cyan-500/25 hover:border-cyan-400 transition-all"
                  data-testid="csd-category-tag"
                  title={`See all ${cs.workshop_category} case studies`}
                >
                  {cs.workshop_category}
                </Link>
              )}
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] mb-4" data-testid="csd-org-name">
              {cs.org_name}
            </h1>
            {cs.engagement_title && (
              <p className="text-xl sm:text-2xl text-violet-300 font-semibold mb-6">{cs.engagement_title}</p>
            )}

            {cs.audience && (
              <div className="flex items-center gap-2 text-slate-400 mb-8">
                <Users className="h-4 w-4 text-cyan-400" />
                <span>Audience: <span className="text-slate-200 font-medium">{cs.audience}</span></span>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={() => window.open(CALENDLY_URL, '_blank')} size="lg" className="bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-500 hover:to-cyan-400 text-white font-bold rounded-xl" data-testid="csd-hero-book">
                <Calendar className="mr-2 h-4 w-4" /> Book a Strategy Session
              </Button>
              <Button onClick={() => navigate('/assessment')} size="lg" variant="outline" className="border-slate-700 text-white hover:bg-white/5 rounded-xl" data-testid="csd-hero-assessment">
                Take WE Alignment Index™
              </Button>
              {cs.website_url && (
                <a
                  href={cs.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center text-cyan-300 hover:bg-cyan-500/10 rounded-xl h-11 px-6 text-base font-medium transition-colors whitespace-nowrap"
                  data-testid="csd-website"
                >
                  <ExternalLink className="mr-2 h-4 w-4" /> Visit Site
                </a>
              )}
            </div>
          </div>

          {/* Right: hero image */}
          <div className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-violet-500/10">
              <img src={hero} alt={cs.org_name} className="w-full h-[360px] sm:h-[420px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 to-transparent pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────── ENGAGEMENT DETAILS ─────────────────────────── */}
      <section className="relative py-16 px-4 sm:px-6 lg:px-8 border-t border-white/5">
        <div className="max-w-4xl mx-auto space-y-12">
          {cs.description && (
            <div>
              <p className="text-violet-400 font-semibold text-sm uppercase tracking-wider mb-3">Engagement Overview</p>
              <p className="text-lg sm:text-xl text-slate-200 leading-relaxed" data-testid="csd-description">{cs.description}</p>
            </div>
          )}

          {cs.outcome && (
            <div className="relative p-7 sm:p-10 rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-cyan-500/10 to-violet-500/5">
              <p className="text-cyan-400 font-bold text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
                <Target className="h-4 w-4" /> Outcome
              </p>
              <p className="text-xl sm:text-2xl text-white leading-relaxed font-medium" data-testid="csd-outcome">{cs.outcome}</p>
            </div>
          )}

          {cs.testimonial_quote && (
            <div className="relative p-7 sm:p-10 rounded-2xl border border-violet-500/30 bg-gradient-to-br from-violet-500/10 to-transparent">
              <Quote className="absolute top-6 right-6 h-10 w-10 text-violet-500/20" />
              <p className="text-xl sm:text-2xl text-slate-100 italic leading-relaxed mb-4" data-testid="csd-testimonial">
                "{cs.testimonial_quote}"
              </p>
              {cs.testimonial_author && (
                <p className="text-sm text-violet-300 font-semibold">— {cs.testimonial_author}</p>
              )}
            </div>
          )}

          {cs.video_url && (
            <div>
              <p className="text-violet-400 font-semibold text-sm uppercase tracking-wider mb-4">Video Testimonial</p>
              <div className="relative aspect-video rounded-2xl overflow-hidden border border-white/10 bg-slate-900">
                <iframe
                  src={cs.video_url}
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={`${cs.org_name} testimonial video`}
                />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ─────────────────────────── RELATED WORKSHOPS ─────────────────────────── */}
      {cs.workshop_category && (
        <section className="relative py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-transparent via-white/[0.02] to-transparent">
          <div className="max-w-4xl mx-auto">
            <div className="rounded-2xl border border-white/10 p-7 sm:p-9 bg-white/[0.02]">
              <p className="text-violet-400 font-semibold text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
                <BookOpen className="h-4 w-4" /> Related Workshops
              </p>
              <h3 className="text-2xl font-bold mb-3">
                Want this kind of impact for your team?
              </h3>
              <p className="text-slate-300 mb-6">
                Explore our <span className="text-cyan-300 font-semibold">{cs.workshop_category}</span> programs and customize one for your organization.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={() => navigate('/workshops')} className="bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-500 hover:to-cyan-400 text-white font-semibold" data-testid="csd-related-workshops">
                  Explore Workshops <ArrowLeft className="ml-2 h-4 w-4 rotate-180" />
                </Button>
                <Button onClick={() => window.open(CALENDLY_URL, '_blank')} variant="outline" className="border-slate-700 text-white hover:bg-white/5">
                  <Calendar className="mr-2 h-4 w-4" /> Bring This to Your Org
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ─────────────────────────── RELATED CASE STUDIES ─────────────────────────── */}
      {related.length > 0 && (
        <section className="relative py-16 px-4 sm:px-6 lg:px-8 border-t border-white/5">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold">More Case Studies</h2>
              <Link to="/case-studies" className="text-cyan-400 hover:text-cyan-300 text-sm font-semibold inline-flex items-center gap-1">
                View all <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="csd-related-grid">
              {related.map((r) => (
                <Link key={r.id} to={`/case-studies/${r.slug}`} className="block focus:outline-none focus:ring-2 focus:ring-violet-500/50 rounded-2xl" data-testid={`csd-related-${r.slug}`}>
                  <CaseStudyCard cs={normalizeCaseStudy(r)} />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─────────────────────────── FINAL CTA ─────────────────────────── */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto rounded-3xl border border-violet-500/30 bg-gradient-to-br from-violet-500/15 via-cyan-500/10 to-violet-500/15 p-10 sm:p-14 text-center relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-80 h-80 bg-violet-500/20 rounded-full blur-[100px]" />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-cyan-500/20 rounded-full blur-[100px]" />
          <div className="relative z-10">
            <Sparkles className="h-6 w-6 text-violet-400 mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Let's build your organization's <span className="bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">next chapter.</span>
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
              Whether you're a school district, agency, or growing company — we'll design an engagement that aligns your team, develops your leaders, and produces real outcomes.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => window.open(CALENDLY_URL, '_blank')} size="lg" className="bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-500 hover:to-cyan-400 text-white font-bold px-8 py-6 text-base rounded-xl" data-testid="csd-final-book">
                <Calendar className="mr-2 h-5 w-5" /> Book a Strategy Session
              </Button>
              <Button onClick={() => navigate('/workshops#workshop-inquiry')} size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/5 px-8 py-6 text-base rounded-xl" data-testid="csd-final-inquiry">
                Request Customized Training
              </Button>
            </div>
            <p className="mt-8 text-xs text-slate-400 flex items-center gap-2 justify-center">
              <Mic className="h-3.5 w-3.5" />
              Available for conferences, retreats, workforce programs, and leadership events.
            </p>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <BrandLogo className="h-9 w-auto" fallbackClassName="text-sm font-bold tracking-wide text-white whitespace-nowrap" testId="csd-footer-logo-img" />
            <span className="text-slate-400 text-sm">© {new Date().getFullYear()} {companyInfo.name}</span>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <a href="/" className="text-slate-400 hover:text-white">Home</a>
            <a href="/workshops" className="text-slate-400 hover:text-white">Workshops</a>
            <a href="/case-studies" className="text-slate-400 hover:text-white">Case Studies</a>
            <a href="/assessment" className="text-slate-400 hover:text-white">Assessment</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
